import { motion, useReducedMotion } from "framer-motion";
import { pctLabel } from "../lib/format";
import { DUR, EASE_OUT } from "../lib/motion";
import Counter from "./Counter";

/**
 * Radial ring gauge for one similarity metric.
 *
 * `role="img"` plus an `aria-label` carries the value for screen readers, and
 * the printed percentage means the reading never depends on the arc or its
 * colour alone. The arc is drawn with `pathLength`, so it animates without
 * touching layout.
 */
function Gauge({ value, label, formula, size = 116, stroke = 9, color = "var(--accent)", delay = 0 }) {
  const prefersReducedMotion = useReducedMotion();
  const safe = Number.isFinite(value) ? Math.min(Math.max(value, 0), 1) : 0;
  const radius = (size - stroke) / 2;
  const center = size / 2;

  return (
    <div className="gauge">
      <div className="gauge__wrap" style={{ width: size, height: size }}>
        <svg
          className="gauge__svg"
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          role="img"
          aria-label={`${label}: ${pctLabel(value)}`}
        >
          <circle
            className="gauge__track"
            cx={center}
            cy={center}
            r={radius}
            strokeWidth={stroke}
          />
          <motion.circle
            className="gauge__arc"
            cx={center}
            cy={center}
            r={radius}
            strokeWidth={stroke}
            stroke={color}
            initial={{ pathLength: prefersReducedMotion ? safe : 0 }}
            whileInView={{ pathLength: safe }}
            viewport={{ once: true, margin: "0px 0px -10% 0px" }}
            transition={
              prefersReducedMotion
                ? { duration: 0 }
                : { duration: DUR.reveal * 1.6, ease: EASE_OUT, delay }
            }
          />
        </svg>
        <span className="gauge__readout" aria-hidden="true">
          <span className="gauge__value">
            <Counter value={safe} mode="percent" digits={1} />
          </span>
          <span className="gauge__unit">percent</span>
        </span>
      </div>
      <span className="gauge__label">{label}</span>
      {formula && <span className="gauge__formula">{formula}</span>}
    </div>
  );
}

export default Gauge;
